import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@shared/ui/card';
import { LabeledInput, LoadingButton } from '@shared/patterns';
import { useAuth } from '@shared/auth/AuthContext';
import { ApiError } from '@shared/api/client';

export const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      const next = searchParams.get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        navigate(next, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected login error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Authentication</CardTitle>
        <CardDescription>Enter your credentials to access tenant suites.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/15 border border-destructive/30 text-xs text-destructive font-medium">
              {error}
            </div>
          )}
          <LabeledInput
            label="Email, Admission No (ADM-...), Employee Code, or Phone"
            type="text"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. rohan@example.com or ADM-2026-0001 or 9876543210"
          />
          <LabeledInput
            label="Password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <LoadingButton type="submit" className="w-full" isLoading={isSubmitting}>
            Sign In
          </LoadingButton>

          <div className="text-center pt-2 text-xs text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Self-Register here
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
